// Makes the PDF behind every "Download PDF" button: a picture of what is on screen (the figures and charts), under a
// heading that names the venue and the period, cut into A4 sheets between blocks. The page capture is loaded only when
// it is asked for.
import { buildImagePdf, jpegBytesFromDataUrl } from './imagePdf';
import { renderReportPage, splitIntoPages } from './reportPage';

// The capture draws text a few pixels below the box it was measured in, so a cut goes this far (in screen pixels) under the
// end of a block, in the empty space between it and the next one, and never through the last line of text.
const CUT_ALLOWANCE = 6;

// A browser cannot make a canvas much taller than this, and some (phones) cannot make one with more pixels than this in all,
// so a very long page is captured at a smaller scale.
const MAX_CAPTURE_HEIGHT = 30000;
const MAX_CAPTURE_AREA = 16000000;

// If the browser still cannot make the picture, it is tried again at these fractions of the scale before giving up.
const RETRY_FACTORS = [1, 0.6, 0.35];

// A table in a scroll window (a long list kept to a fixed height on screen) would be captured as only the part that shows.
// A text box scrolls on its own and is drawn as it is, so it is not one of these.
const scrolls = (style, node) => !node.matches('textarea, select') && (/(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflowX));

// How much taller the page is once every scroll window is opened out in full.
const hiddenHeight = (root) => Array.from(root.querySelectorAll('*')).reduce((sum, node) => (
    scrolls(window.getComputedStyle(node), node) ? sum + Math.max(node.scrollHeight - node.clientHeight, 0) : sum), 0);

// On the copy the capture draws, opens every scroll window out to its full height and width, so the whole list or map is in
// the picture. A window that scrolls sideways (the hours of the heat map on a narrow screen) is wider than the page, so the
// copy is made wide enough to hold it, with the same space to its right that it has now.
const openScrollWindows = (clone) => {
    const view = clone.ownerDocument.defaultView;
    const windows = [clone, ...clone.querySelectorAll('*')].filter((node) => scrolls(view.getComputedStyle(node), node));
    const page = clone.getBoundingClientRect();
    const needed = windows.reduce((widest, node) => {
        if (node === clone || node.scrollWidth <= node.clientWidth) return widest;
        const box = node.getBoundingClientRect();
        return Math.max(widest, box.left - page.left + node.scrollWidth + (page.right - box.right));
    }, page.width);
    windows.forEach((node) => {
        node.style.overflow = 'visible';
        node.style.maxHeight = 'none';
        node.style.height = 'auto';
    });
    if (needed > page.width) {
        clone.style.width = `${Math.ceil(needed)}px`;
        clone.style.maxWidth = 'none';
    }
};

// The scale to capture at: sharp where the page is small, and smaller as it gets long or wide enough to reach the limits above.
const captureScale = (blocks) => {
    const height = Math.max(blocks.reduce((sum, item) => sum + item.offsetHeight + hiddenHeight(item), 0), 1);
    const width = Math.max(...blocks.map((item) => Math.max(item.offsetWidth, item.scrollWidth)), 1);
    const sharp = Math.max(2, window.devicePixelRatio || 1);
    return Math.max(0.35, Math.min(sharp, MAX_CAPTURE_HEIGHT / height, Math.sqrt(MAX_CAPTURE_AREA / (height * width))));
};

// Makes the sheets at one scale.
async function captureSheets({ html2canvas, element, extras, venueName, reportName, rangeText, scale }) {
    // Where each block of the report ends, and where each header (the head of a table, or anything marked
    // data-repeat-head, such as the weekday names above a calendar) is with the rows under it, measured on the copy the
    // capture draws (without the buttons), so a page can be cut between blocks and a header repeated on the sheets its
    // rows continue on. Each data-print-keep group is measured too.
    let blockEnds = [];
    let tables = [];
    let groups = [];
    const main = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale,
        logging: false,
        onclone: (_document, clone) => {
            openScrollWindows(clone);
            const top = clone.getBoundingClientRect().top;
            blockEnds = Array.from(clone.querySelectorAll('[data-print-block]')).map((block) => (block.getBoundingClientRect().bottom - top) * scale);
            groups = Array.from(clone.querySelectorAll('[data-print-keep]')).map((group) => {
                const box = group.getBoundingClientRect();
                return { start: (box.top - top) * scale, end: (box.bottom - top) * scale };
            });
            const headed = [
                ...Array.from(clone.querySelectorAll('table')).filter((table) => table.tHead).map((table) => [table.tHead, table]),
                ...Array.from(clone.querySelectorAll('[data-repeat-head]')).map((head) => [head, head.parentElement]),
            ];
            tables = headed.map(([header, whole]) => {
                const head = header.getBoundingClientRect();
                return { headTop: (head.top - top) * scale, headEnd: (head.bottom - top) * scale, end: (whole.getBoundingClientRect().bottom - top) * scale };
            });
        },
    });
    const sections = [main];
    for (const extra of extras) sections.push(await html2canvas(extra, { backgroundColor: '#ffffff', scale, logging: false, onclone: (_document, clone) => openScrollWindows(clone) }));

    const page = renderReportPage({ captured: sections, venueName, reportName, rangeText });
    // The end of every section is a place to cut, as well as the ends of the blocks inside the first one.
    const breaks = [...blockEnds.map((end) => end + page.tops[0]), ...sections.map((section, index) => page.tops[index] + section.height)].map((end) => end + CUT_ALLOWANCE * scale);
    const repeats = tables.map((table) => ({ headTop: table.headTop + page.tops[0], headEnd: table.headEnd + page.tops[0], end: table.end + page.tops[0] }));
    // A group (data-print-keep) stays on one sheet if it fits on one: the cut places just under the block before it and under its own end are
    // still allowed (they sit CUT_ALLOWANCE under the ends), only those inside it are not.
    const keeps = groups.map((group) => ({ start: group.start + page.tops[0] + CUT_ALLOWANCE * scale, end: group.end + page.tops[0] + CUT_ALLOWANCE * scale }));
    // A white page, so each sheet can be stored as a JPEG inside the PDF.
    return splitIntoPages(page.canvas, breaks, undefined, repeats, keeps).map((sheet) => ({ jpeg: jpegBytesFromDataUrl(sheet.toDataURL('image/jpeg', 0.92)), width: sheet.width, height: sheet.height }));
}

// Returns the bytes of the PDF. `element` is the block to capture; `extras` are further blocks (their own elements on
// the page) that follow it on the page of the PDF, each kept whole.
export async function captureReport({ element, extras = [], venueName, reportName, rangeText }) {
    const { default: html2canvas } = await import('html2canvas');
    const scale = captureScale([element, ...extras]);
    let failure;
    for (const factor of RETRY_FACTORS) {
        try {
            return buildImagePdf(await captureSheets({ html2canvas, element, extras, venueName, reportName, rangeText, scale: scale * factor }));
        } catch (error) {
            failure = error; // most likely the browser could not make a picture this large: try a smaller one
        }
    }
    throw failure;
}

export function savePdf(bytes, filename) {
    const link = document.createElement('a');
    link.download = filename;
    link.href = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 10000);
}
